import React from 'react';
import noop from 'lodash/noop';

import type { IconComponentFunction } from '@motorway/mw-highway-code';
import { ArrowDownIcon, IconText } from '@motorway/mw-highway-code';
import { useExpandable } from '@motorway/mw-highway-code/alpha';
import { useIsomorphicLayoutEffect } from '@motorway/mw-highway-code/hooks';

import { SNOWPLOW_EVENTS } from 'Services/analytics/analytics.const';
import { motorwayPayPageEvents } from 'Services/analytics/events/paymentEvents/motorwayPayPageEvents';
import { applyTestData } from 'Utilities/helpers';
import { useIsMounted } from 'Utilities/hooks/useIsMounted';

import styles from './FAQSection.module.scss';

export const FAQExpandable: React.FC<{
	content: React.ReactNode;
	dataTestId?: string;
	defaultIsExpanded: boolean;
	heading: React.ReactNode;
	icon: IconComponentFunction;
	id: string;
	onExpandChange?: ({ id, isExpanded }: { id: string; isExpanded: boolean }) => void;
}> = ({ content, dataTestId, defaultIsExpanded, heading, icon, id, onExpandChange }) => {
	const { buttonProps, regionProps } = useExpandable({
		disabled: false,
		id,
		isExpanded: defaultIsExpanded,
		onClick: noop,
	});
	const isMounted = useIsMounted();

	const isExpanded = buttonProps['aria-expanded'] === true;

	useIsomorphicLayoutEffect(() => {
		if (!isMounted) {
			return;
		}

		if (onExpandChange) {
			onExpandChange({
				id,
				isExpanded,
			});

			return;
		}

		motorwayPayPageEvents<{
			label: string;
		}>({
			customData: {
				label: isExpanded ? `opened - ${heading}` : `closed - ${heading}`,
			},
			name: 'FAQs clicked',
			schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
			version: SNOWPLOW_EVENTS.CLICK.VERSION,
		});
	}, [isExpanded, heading, isMounted]);

	return (
		<div className={styles.expandable}>
			<div
				{...buttonProps}
				className={styles.header}
				{...applyTestData({ testid: dataTestId ? `${dataTestId}-btn` : undefined })}
			>
				<IconText
					icon={(props) => <div className={styles.icon}>{icon(props)}</div>}
					text={heading}
					variant="small--heading5"
				/>
				<ArrowDownIcon data-direction={buttonProps['aria-expanded'] ? 'up' : 'down'} />
			</div>
			<div {...regionProps} className={styles.main} {...applyTestData({ testid: dataTestId || undefined })}>
				{content}
			</div>
		</div>
	);
};
